import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useBookingStore } from '@/stores/booking'
import { useUrlParams } from '@/composables/useUrlParams'
import type { GuestInfo } from '@/types/booking.types'
import { useDateFormatting } from './useDateFormatting'

export function useBookingFlow() {
  const { t } = useI18n()
  const router = useRouter()
  const bookingStore = useBookingStore()
  const { getQueryParams } = useUrlParams()

  // Reactive state
  const selectedDate = ref<Date | null>(null)
  const selectedTime = ref<string | null>(null)
  const partySize = ref(2)
  const availableTimes = ref<string[]>([])
  const loadingTimes = ref(false)
  const isSubmitting = ref(false)
  const isBookingInProgress = ref(false)
  const lastSubmitTime = ref(0)
  const error = ref('')
  const formValidated = ref(false)
  const initialLoading = ref(true)
  const termsAccepted = ref(false)
  const specialRequests = ref<string>('')

  // Initialize guest info
  const guestInfo = ref<GuestInfo>({
    name: '',
    mobile: {
      countryCode: '',
      number: ''
    }
  })

  // Get the date formatting functions from our consolidated composable
  const {
    formatDate,
    formatTime: formatTimeUtil,
    formatBookingDate
  } = useDateFormatting()

  // Computed properties for formatted date and time
  const formattedDate = computed(() => {
    if (!selectedDate.value) return '--'
    return formatDate(selectedDate.value)
  })

  const formattedTime = computed(() => {
    if (!selectedTime.value) return '--'
    return formatTimeUtil(selectedTime.value)
  })

  // Computed property for maxPartySize from the store
  const maxPartySize = computed(() => {
    return bookingStore.maxPartySize || 20 // Fallback to 20 if not set
  })

  // Formatted date for the collapsed summary view
  const formattedSelectedDate = computed(() => {
    if (!selectedDate.value) return ''
    return formatBookingDate(selectedDate.value)
  })

  // Formatted date without year
  const formattedDateWithoutYear = computed(() => {
    if (!selectedDate.value) return '--'
    return formatBookingDate(selectedDate.value)
  })

  // Check if form is valid
  const shouldEnableButton = computed(() => {
    return !!selectedDate.value &&
          !!selectedTime.value &&
          !!guestInfo.value.name &&
          !!guestInfo.value.mobile?.number &&
          termsAccepted.value
  })

  // Methods for party size
  const changePartySize = async (increment: boolean) => {
    // Get the maxPartySize from the store
    const maxPartySize = bookingStore.maxPartySize || 20 // Fallback to 20 if not set

    if ((increment && partySize.value >= maxPartySize) || (!increment && partySize.value <= 1)) {
      return
    }

    const previousPartySize = partySize.value
    const currentDate = selectedDate.value
    const currentTime = selectedTime.value

    partySize.value = increment ? partySize.value + 1 : partySize.value - 1

    try {
      // Update party size in store and refresh availability data
      // Let the store handle loading indicators and cache management
      await bookingStore.setPartySize(partySize.value)

      // Handle selection validity logic
      if (currentDate && currentTime) {
        const stillValid = bookingStore.checkSelectionValidityForNewPartySize(
          partySize.value, currentDate, currentTime
        )

        if (!stillValid) {
          // Time is invalid, but let's check if the date still has availability
          const dateHasAvailability = bookingStore.checkDateHasAvailabilityForPartySize(
            partySize.value, currentDate
          )

          if (dateHasAvailability) {
            // Keep the date but reset the time since selected time is no longer available
            selectedTime.value = null

            // Get times for the selected date with new party size
            refreshAvailableTimes(currentDate)
          } else {
            // No times available for this date with new party size, reset both selections
            selectedDate.value = null
            selectedTime.value = null
          }
        }
      } else if (currentDate && !currentTime) {
        // If date is selected but no time, refresh available times for new party size

        // First check if the date still has availability with the new party size
        const dateHasAvailability = bookingStore.checkDateHasAvailabilityForPartySize(
          partySize.value, currentDate
        )

        if (!dateHasAvailability) {
          // No times available for this date with new party size, reset date selection
          selectedDate.value = null
          return
        }

        refreshAvailableTimes(currentDate)
      }
    } catch (err) {
      console.error('Error updating party size:', err instanceof Error ? err.message : 'Unknown error')
      error.value = t('common.error')
      // Revert party size back on error
      partySize.value = previousPartySize
    }
  }

  // Helper method to refresh available times
  const refreshAvailableTimes = async (date: Date) => {
    // Get times for the selected date with current party size from cache first
    const selectedDateInfo = bookingStore.availableDates.find(d => {
      const dateObj = new Date(d.date)
      return dateObj.toDateString() === date.toDateString()
    })

    if (selectedDateInfo && selectedDateInfo.availableTimes.length > 0) {
      // Use cached times from store for better performance
      availableTimes.value = selectedDateInfo.availableTimes
    } else {
      // No cached data available, fetch from API with loading state
      loadingTimes.value = true
      availableTimes.value = [] // Reset times

      try {
        availableTimes.value = await bookingStore.getAvailableTimesForDate(date)
      } catch (err) {
        error.value = t('common.error')
        console.error('Error fetching available times:', err instanceof Error ? err.message : 'Unknown error')
      } finally {
        loadingTimes.value = false
      }
    }
  }

  // Create booking
  const createBooking = async () => {
    if (!selectedDate.value || !selectedTime.value) {
      error.value = t('booking.missingDateOrTime')
      return null
    }

    if (!guestInfo.value.name || !guestInfo.value.mobile?.number) {
      error.value = t('booking.missingRequiredFields')
      return null
    }

    // Set both flags to prevent duplicate submissions
    isSubmitting.value = true
    isBookingInProgress.value = true

    // Creating booking with validated form data

    error.value = ''

    try {
      // Update store with current selections
      bookingStore.selectDate(selectedDate.value)
      bookingStore.selectTime(selectedTime.value)
      bookingStore.setPartySize(partySize.value)

      // Include special requests in the guest info
      const guestInfoWithRequests = {
        ...guestInfo.value,
        specialRequests: specialRequests.value
      }
      bookingStore.setGuestInfo(guestInfoWithRequests)

      // Create the booking
      const bookingResponse = await bookingStore.createBooking()

      // Release the submitting flag
      isSubmitting.value = false

      // Add a slight delay before allowing another submission
      setTimeout(() => {
        isBookingInProgress.value = false
      }, 1000)

      // If we have a booking response and a temporary booking created
      if (bookingResponse && bookingStore.temporaryBooking) {
        // Generate a booking ID
        const bookingId = bookingStore.temporaryBooking.id

        // CRITICAL: Store the booking response in the store
        // This ensures it's available to the confirmation/deposit pages
        bookingStore.setLastBookingResponse(bookingResponse)

        // Navigate to appropriate booking status page based on member status and deposit requirement
        // This routing logic ensures users see the correct flow for their membership and payment status
        if (bookingResponse.member) {
          // Member flow - direct routing based on deposit requirement
          if (bookingResponse.deposit) {
            // Member with deposit required - route directly to deposit page
            router.replace({
              name: 'member-deposit-pending',
              params: { bookingId },
              query: getQueryParams()
            })
          } else {
            // Member without deposit - route directly to confirmation page
            router.replace({
              name: 'member-booking-confirmation',
              params: { bookingId },
              query: getQueryParams()
            })
          }
        } else {
          // Non-member flow - route directly to non-member pending page
          router.replace({
            name: 'nonMemberPending',
            params: { bookingId },
            query: getQueryParams()
          })
        }
      }

      return bookingResponse
    } catch (err) {
      console.error('Error creating booking:', err instanceof Error ? err.message : 'Unknown error')
      error.value = t('common.error')

      // Check if there's a more specific error in the store
      if (bookingStore.error) {
        error.value = bookingStore.error
      }

      return null
    } finally {
      isSubmitting.value = false

      // Add a slight delay before allowing another submission
      setTimeout(() => {
        isBookingInProgress.value = false
      }, 1000)
    }
  }

  return {
    // State
    selectedDate,
    selectedTime,
    partySize,
    availableTimes,
    loadingTimes,
    isSubmitting,
    isBookingInProgress,
    lastSubmitTime,
    error,
    formValidated,
    initialLoading,
    termsAccepted,
    specialRequests,
    guestInfo,

    // Computed
    formattedDate,
    formattedTime,
    formattedSelectedDate,
    formattedDateWithoutYear,
    shouldEnableButton,
    maxPartySize,

    // Methods
    increasePartySize: () => changePartySize(true),
    decreasePartySize: () => changePartySize(false),
    createBooking,
    refreshAvailableTimes
  }
}